import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import {
  useTSQPurchOrderList,
  useTSMDeactivatePO,
  useTSMReactivatePO,
  useTSMUpdatePurchOrderApproval,
  useTSMCompletePO,
  useTSMVoidPO,
} from "../../hooks/useTSPurchaseOrder";
import { IdAsUUID, ListRequestFilter } from "../../types/common/commonTypes";
import {
  PurchaseOrderReceiptStatusType,
  PurchOrderFull,
  PurchOrderStatusUpdate,
} from "../../types/purchOrderTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import {
  ApproveIcon,
  RejectIcon,
} from "../../components/common/NucleusSymbolsAndIcons";
import { NumericDisplayField } from "../../components/common/CustomFields";
import stringUtils from "../../utils/stringUtils";
import statusUtils from "../../utils/statusUtils";
import {
  ApprovalStatusType,
  PrimaryOrderStatusType,
} from "../../types/common/statusTypes";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";

interface POData {
  id: IdAsUUID;
  number: string;
  orderStatus: PrimaryOrderStatusType;
  approvalStatus: ApprovalStatusType;
  receiptStatus: PurchaseOrderReceiptStatusType;
  subtotal: number;
  vendor: string;
  orderDate: Date;
  isActive: boolean;
}

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PagePurchOrderList: React.FC = () => {
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [targetNum, setTargetNum] = useState<string>("");
  const [targetVendor, setTargetVendor] = useState<string>("");

  const [orderBy, setOrderBy] = useState<string>("number");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "number":
        return [
          {
            table: "purchase_orders",
            alt_name: "po",
            field: "number",
            asc: order === "asc",
          },
        ];
      case "vendor":
        return [
          {
            table: "vendors",
            alt_name: "vend",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "purchase_orders",
            alt_name: "po",
            field: "number",
            asc: false,
          },
        ];
      case "order_date":
        return [
          {
            table: "purchase_orders",
            alt_name: "po",
            field: "order_date",
            asc: order === "asc",
          },
        ];
      default:
        return [];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetVendor !== "") {
      filts[0] = [];
      filts[1] = [];
      filts[0].push({
        table: "vendors",
        alt_name: "vend",
        field: "name",
        operator: "contains",
        value: targetVendor,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "vendors",
        alt_name: "vend",
        field: "code",
        operator: "contains",
        value: targetVendor,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetNum !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "purchase_orders",
        alt_name: "po",
        field: "number",
        operator: "contains",
        value: targetNum,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "purchase_orders",
        alt_name: "po",
        field: "number",
        operator: "contains",
        value: targetNum,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    // if (targetLot !== "") {
    //   if (filts[0] === undefined) {
    //     filts[0] = [];
    //   }
    //   filts[0].push({
    //     table: "employees",
    //     alt_name: "rep_to",
    //     field: "first_name",
    //     operator: "contains",
    //     value: targetLot,
    //     is_not: false,
    //     inclusive_start: false,
    //     inclusive_end: false,
    //     case_insensitive: true,
    //   });
    //   if (filts[1] === undefined) {
    //     filts[1] = [];
    //   }
    //   filts[1].push({
    //     table: "employees",
    //     alt_name: "rep_to",
    //     field: "last_name",
    //     operator: "contains",
    //     value: targetLot,
    //     is_not: false,
    //     inclusive_start: false,
    //     inclusive_end: false,
    //     case_insensitive: true,
    //   });
    // }
    return filts;
  }, [targetNum, targetVendor /*, targetCF*/]);

  const {
    data: poResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "purchase_orders",
    alt_name: "po",
    primary_fields: [
      "id",
      "number",
      "order_date",
      "order_status",
      "approval_status",
      "receipt_status",
      "is_record_active",
    ],
    joins: [
      {
        table: "vendors",
        alt_name: "vend",
        fields: ["name", "code"],
        condition: {
          remote_table: "purchase_orders",
          alt_name: "po",
          remote_field: "vendor_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [
      {
        result_name: "po.subtotal",
        aggregation: {
          function: "sum",
          table: "purchase_order_line_items",
          alt_name: null,
          field: null,
          expression: "unit_cost * quantity_ordered",
        },
        from_table: "purchase_order_line_items",
        from_alt_name: null,
        join_conditions: [
          {
            remote_table: "purchase_orders",
            alt_name: "po",
            remote_field: "id",
            local_field: "purchase_order_id",
          },
        ],
        filters: null,
      },
    ],
  });

  const [orders, orderTotal]: [POData[], number] = useMemo(() => {
    const orders: POData[] = [];
    const total = poResponse?.total || 0;
    if (poResponse?.items) {
      poResponse.items.forEach((val) => {
        orders.push({
          id: val["po.id"],
          number: val["po.number"],
          orderStatus: val["po.order_status"],
          approvalStatus: val["po.approval_status"],
          receiptStatus: val["po.receipt_status"],
          subtotal: val["po.subtotal"],
          vendor: val["vend.name"],
          orderDate: new Date(val["po.order_date"]),
          isActive: val["po.is_record_active"],
        });
      });
    }

    return [orders, total];
  }, [poResponse]);

  const deactivatePOMutation = useTSMDeactivatePO();
  const reactivatePOMutation = useTSMReactivatePO();
  const updatePOApprovalMutation = useTSMUpdatePurchOrderApproval();
  const completePOMutation = useTSMCompletePO();
  const voidPOMutation = useTSMVoidPO();

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this PO?")) {
      return;
    }

    try {
      await deactivatePOMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate PO:", err);
      setError(err.response?.data?.detail || "Failed to deactivate PO");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivatePOMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate PO:", err);
      setError(err.response?.data?.detail || "Failed to reactivate PO");
    }
  };

  const approveOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot Approve Order");
      return;
    }
    const updatePayload: PurchOrderStatusUpdate = {
      id: id,
      new_status: "APPROVED",
    };
    try {
      await updatePOApprovalMutation.mutateAsync({
        id: id,
        data: updatePayload,
      });
    } catch (e: any) {
      setError("There was an error updating PO approval Status");
      console.error(e);
      return;
    }
  };

  const rejectOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot Reject Order");
      return;
    }
    const updatePayload: PurchOrderStatusUpdate = {
      id: id,
      new_status: "REJECTED",
    };
    try {
      await updatePOApprovalMutation.mutateAsync({
        id: id,
        data: updatePayload,
      });
    } catch (e: any) {
      setError("There was an error updating PO approval Status");
      console.error(e);
      return;
    }
  };

  const completeOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot Complete Order");
      return;
    }
    try {
      await completePOMutation.mutateAsync(id);
    } catch (e: any) {
      setError("There was an error marking PO Complete");
      console.error(e);
      return;
    }
  };

  const voidOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot VOID Order");
      return;
    }
    try {
      await voidPOMutation.mutateAsync(id);
    } catch (e: any) {
      setError("There was an error marking PO VOID");
      console.error(e);
      return;
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  if (isLoading && orders.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/orders/purchase/form?mode=create")}
        >
          Create New PO
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <FilterBox finalSet={setTargetNum} placeholder="Filter By PO Number" />
        <FilterBox
          finalSet={setTargetVendor}
          placeholder="Filter By Vendor Code/Name"
        />
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={orderTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "number" ? order : false}>
                <TableSortLabel
                  active={orderBy === "number"}
                  direction={orderBy === "number" ? order : "asc"}
                  onClick={() => handleSort("number")}
                >
                  PO Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "vendor" ? order : false}>
                <TableSortLabel
                  active={orderBy === "vendor"}
                  direction={orderBy === "vendor" ? order : "asc"}
                  onClick={() => handleSort("vendor")}
                >
                  Vendor
                </TableSortLabel>
              </TableCell>
              <TableCell>Order Status</TableCell>
              <TableCell>Approval Status</TableCell>
              <TableCell>Receipt Status</TableCell>
              <TableCell>Subtotal</TableCell>
              <TableCell
                sortDirection={orderBy === "order_date" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "order_date"}
                  direction={orderBy === "order_date" ? order : "asc"}
                  onClick={() => handleSort("order_date")}
                >
                  Order Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No Purchase Orders found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((po) => (
                <TableRow key={po.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {po.number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {po.vendor}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(po.orderStatus)}
                      color={statusUtils.getPrimaryOrderStatusColor(
                        po.orderStatus,
                      )}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(po.approvalStatus)}
                      color={statusUtils.getApprovalStatusColor(
                        po.approvalStatus,
                      )}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(po.receiptStatus)}
                      color={statusUtils.getPoReceiptStatusColor(
                        po.receiptStatus,
                      )}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <NumericDisplayField
                      value={po.subtotal}
                      isCurrency
                      decimalPlaces={4}
                      typographyProps={{ variant: "body1" }}
                    />
                  </TableCell>
                  <TableCell>{po.orderDate.toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={1}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/orders/purchase/form?mode=view&id=${po.id}`,
                              )
                            }
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit PO">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/orders/purchase/form?mode=edit&id=${po.id}`,
                              )
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!["COMPLETE", "VOID"].includes(po.orderStatus) && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Complete PO">
                              <IconButton
                                size="small"
                                onClick={() => completeOrder(po.id)}
                                color="success"
                                disabled={completePOMutation.isPending}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="VOID PO">
                              <IconButton
                                size="small"
                                onClick={() => voidOrder(po.id)}
                                color="error"
                                disabled={voidPOMutation.isPending}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                      {po.approvalStatus === "PENDING" && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Approve PO">
                            <IconButton
                              size="small"
                              onClick={() => approveOrder(po.id)}
                              color="success"
                              disabled={updatePOApprovalMutation.isPending}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject PO">
                            <IconButton
                              size="small"
                              onClick={() => rejectOrder(po.id)}
                              color="error"
                              disabled={updatePOApprovalMutation.isPending}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {!po.isActive ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Reactivate PO">
                            <IconButton
                              size="small"
                              onClick={() => handleReactivate(po.id)}
                              color="success"
                              disabled={reactivatePOMutation.isPending}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="Deactivate PO">
                            <IconButton
                              size="small"
                              onClick={() => handleDeactivate(po.id)}
                              color="error"
                              disabled={deactivatePOMutation.isPending}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={orderTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
